import React, {
  createContext,
  useContext, useEffect,
  useState,
} from 'react';
import {FormikProvider, useFormik} from "formik";
import debounce from "debounce";
import dayjs from "dayjs";
import {getList} from "../actions/actions";
import {convertBinaryToString} from "../../../common/utils/form/convertBinaryToString";
import {getSalePricePart} from "../../../common/utils/form/getSalePricePart";

const TaxAuthorityContext = createContext(null);

export const useTaxAuthorityContext = () => useContext(TaxAuthorityContext);

export const TaxAuthorityProvider = ({children, cities, streets, updateStreets}) => {
  const [page, setPage] = useState(0);
  const [data, setData] = useState([]);

  // main filters
  const [city, setCity] = useState('');
  const [street, setStreet] = useState('');
  const [houseNumber, setHouseNumber] = useState('');
  const [fromGush, setFromGush] = useState('');
  const [toGush, setToGush] = useState('');
  const [fromHelka, setFromHelka] = useState('');
  const [toHelka, setToHelka] = useState('');
  const [propertyType, setPropertyType] = useState('');
  const [fromSalePrice, setFromSalePrice] = useState('');
  const [toSalePrice, setToSalePrice] = useState('');
  const [fromSaleDate, setFromSaleDate] = useState('');
  const [toSaleDate, setToSaleDate] = useState('');
  const [fromRoomsCount, setFromRoomsCount] = useState('');
  const [toRoomsCount, setToRoomsCount] = useState('');
  const [fromRegisteredArea, setFromRegisteredArea] = useState('');
  const [toRegisteredArea, setToRegisteredArea] = useState('');
  //extra
  const [fromEstimatePrice, setFromEstimatePrice] = useState('');
  const [toEstimatePrice, setToEstimatePrice] = useState('');
  const [sellType, setSellType] = useState('');
  const [floor, setFloor] = useState('');
  const [floorsCount, setFloorsCount] = useState('');
  const [fromBuildYear, setFromBuildYear] = useState('');
  const [toBuildYear, setToBuildYear] = useState('');
  const [buildingAge, setBuildingAge] = useState('');
  const [buildingUnitFunction, setBuildingUnitFunction] = useState('');
  const [fromMunicipalArea, setFromMunicipalArea] = useState('');
  const [toMunicipalArea, setToMunicipalArea] = useState('');
  const [soldShare, setSoldShare] = useState('');
  const [parking, setParking] = useState('');
  const [elevator, setElevator] = useState('');
  const [gallery, setGallery] = useState('');
  const [yard, setYard] = useState('');
  const [storage, setStorage] = useState('');
  const [roof, setRoof] = useState('');
  const [lot, setLot] = useState('');
  const [removeDuplicates, setRemoveDuplicates] = useState('');

  const handleSearchChange = (key, handler) => value => {
    setPage(0);
    handler(value);
  };
  const handleCityChange = value => {
    setPage(0);
    setCity(value);
    handleSearchReset('street', setStreet)();
    if (Boolean(value.value)) {
      updateStreets(value.value);
    } else {
      handleCityReset();
    }
  };
  const handleSearchReset = (key, handler) => () => {
    handleSearchChange(key, handler)(null);
  };
  const handleCityReset = () => {
    handleSearchReset('city', setCity)();
    handleSearchReset('street', setStreet)();
  };

  const requestBody = {
    shumaId: 0, // permission for all users
    city: (city) ? city.label : '',
    street: (street) ? street.label : '',
    houseNumber: houseNumber || '',
    fromGush: fromGush || '',
    toGush: toGush || '',
    fromHelka: fromHelka || '',
    toHelka: toHelka || '',
    propertyType: propertyType || '',
    fromSalePrice: fromSalePrice || '',
    toSalePrice: toSalePrice || '',
    fromSaleDate: fromSaleDate ? dayjs(fromSaleDate).format('YYYY-MM-DD') : '',
    toSaleDate: toSaleDate ? dayjs(toSaleDate).format('YYYY-MM-DD') : '',
    fromRegisteredArea: fromRegisteredArea || '',
    toRegisteredArea: toRegisteredArea || '',
    fromRoomsCount: fromRoomsCount || '',
    toRoomsCount: toRoomsCount || '',
    fromEstimatePrice: fromEstimatePrice || '',
    toEstimatePrice: toEstimatePrice || '',
    sellType: sellType || '',
    floor: floor || '',
    floorsCount: floorsCount || '',
    fromBuildYear: fromBuildYear ? dayjs(fromBuildYear).format('YYYY') : '',
    toBuildYear: toBuildYear ? dayjs(toBuildYear).format('YYYY') : '',
    buildingAge: buildingAge || '',
    buildingUnitFunction: buildingUnitFunction || '',
    fromMunicipalArea: fromMunicipalArea || '',
    toMunicipalArea: toMunicipalArea || '',
    soldShare: soldShare ? 1 : 0,
    parking: parking ? 1 : 0,
    elevator: elevator ? 1 : 0,
    gallery: gallery ? 1 : 0,
    yard: yard ? 1 : 0,
    storage: storage ? 1 : 0,
    roof: roof ? 1 : 0,
    lot: lot ? 1 : 0,
    removeDuplicates: removeDuplicates ? 1 : 0
  };

  const refreshGrid = debounce(async () => {
    const result = await getList({...requestBody, requestedPageNumber: page});
    setData(result)
  }, 500);

  useEffect(() => {
    refreshGrid();
  }, [
    page,
    city, street, houseNumber,
    fromGush, toGush,
    fromHelka, toHelka,
    propertyType, fromSalePrice, toSalePrice,
    fromSaleDate, toSaleDate,
    fromRegisteredArea, toRegisteredArea,
    fromRoomsCount, toRoomsCount,
    fromEstimatePrice, toEstimatePrice,
    sellType, floor, floorsCount,
    fromBuildYear, toBuildYear, buildingAge, buildingUnitFunction,
    fromMunicipalArea, toMunicipalArea,
    soldShare, parking, elevator, gallery, yard,
    storage, roof, lot, removeDuplicates,
  ]);

  const formik = useFormik({
    initialValues: {
      city: -1,
      street: -1,
      houseNumber: '',
      fromGush: '',
      toGush: '',
      fromHelka: '',
      toHelka: '',
      propertyType: '',
      fromSalePrice: '',
      toSalePrice: '',
      fromSaleDate: '',
      toSaleDate: '',
      fromRegisteredArea: '',
      toRegisteredArea: '',
      fromRoomCount: '',
      toRoomCount: '',
      fromEstimatePrice: '',
      toEstimatePrice: '',
      transactionType: '',
      floor: '',
      floorsCount: '',
      fromBuildYear: '',
      toBuildYear: '',
      buildingAge: '',
      buildingUnitFunction: '',
      fromMunicipalArea: '',
      toMunicipalArea: '',
      soldShare: false,
      parking: false,
      elevator: false,
      gallery: false,
      yard: false,
      storage: false,
      roof: false,
      lot: false,
      removeDuplicates: false,
    },
    onSubmit: () => {
    },
    enableReinitialize: true,
  });

  const preparedList = Array.isArray(data?.list)
    ? data.list.map(item => {
      const {
        elevator, storage, lot, gallery, saleDate,
        netAreaInSquareMeters, roomCount, salePrice, soldShare,
        ...rest
      } = item;

      return {
        saleDate: saleDate ? dayjs(saleDate).format('DD-MM-YYYY') : '',
        elevator: convertBinaryToString(elevator),
        storage: convertBinaryToString(storage),
        lot: convertBinaryToString(lot),
        gallery: convertBinaryToString(gallery),
        ...rest,
        netAreaInSquareMeters,
        roomCount,
        salePrice,
        soldShare,
        areaRooms: netAreaInSquareMeters && roomCount
          ? (netAreaInSquareMeters/roomCount).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})
          : '',
        salePriceSoldShare: salePrice && soldShare
          ? (Number(salePrice.replace(/,/g, "")) * getSalePricePart(soldShare)).toLocaleString('en-US', {minimumFractionDigits: 0, maximumFractionDigits: 2})
          : ''
      };
    })
    : [];

  const value = {
    data,
    setData,
    list: preparedList,
    totalResultCount: data?.totalResultCount ?? 0,
    totalPageCount: data?.totalPageCount ?? 0,
    page,
    setPage,
    cities, streets,
    search: {
      city: {
        value: city,
        onChange: handleCityChange,
        onReset: handleCityReset,
      },
      street: {
        value: street,
        onChange: handleSearchChange('street', setStreet),
        onReset: handleSearchReset('street', setStreet),
      },
      houseNumber: {
        value: houseNumber,
        onChange: handleSearchChange('houseNumber', setHouseNumber),
      },
      fromGush: {
        value: fromGush,
        onChange: handleSearchChange('fromGush', setFromGush),
      },
      toGush: {
        value: toGush,
        onChange: handleSearchChange('toGush', setToGush),
      },
      fromHelka: {
        value: fromHelka,
        onChange: handleSearchChange('fromHelka', setFromHelka),
      },
      toHelka: {
        value: toHelka,
        onChange: handleSearchChange('toHelka', setToHelka),
      },
      propertyType: {
        value: propertyType,
        onChange: handleSearchChange('propertyType', setPropertyType),
        onReset: handleSearchReset('propertyType', setPropertyType),
      },
      fromSalePrice: {
        value: fromSalePrice,
        onChange: handleSearchChange('fromSalePrice', setFromSalePrice),
      },
      toSalePrice: {
        value: toSalePrice,
        onChange: handleSearchChange('toSalePrice', setToSalePrice),
      },
      fromSaleDate: {
        value: fromSaleDate,
        onChange: handleSearchChange('fromSaleDate', setFromSaleDate),
        onReset: handleSearchReset('fromSaleDate', setFromSaleDate),
      },
      toSaleDate: {
        value: toSaleDate,
        onChange: handleSearchChange('toSaleDate', setToSaleDate),
        onReset: handleSearchReset('toSaleDate', setToSaleDate),
      },
      fromRegisteredArea: {
        value: fromRegisteredArea,
        onChange: handleSearchChange('fromRegisteredArea', setFromRegisteredArea),
      },
      toRegisteredArea: {
        value: toRegisteredArea,
        onChange: handleSearchChange('toRegisteredArea', setToRegisteredArea),
      },
      fromRoomsCount: {
        value: fromRoomsCount,
        onChange: handleSearchChange('fromRoomCount', setFromRoomsCount),
      },
      toRoomsCount: {
        value: toRoomsCount,
        onChange: handleSearchChange('toRoomsCount', setToRoomsCount),
      },
      //extra
      fromEstimatePrice: {
        value: fromEstimatePrice,
        onChange: handleSearchChange('fromEstimatePrice', setFromEstimatePrice),
      },
      toEstimatePrice: {
        value: toEstimatePrice,
        onChange: handleSearchChange('toEstimatePrice', setToEstimatePrice),
      },
      sellType: {
        value: sellType,
        onChange: handleSearchChange('sellType', setSellType),
        onReset: handleSearchReset('sellType', setSellType),
      },
      floor: {
        value: floor,
        onChange: handleSearchChange('floor', setFloor),
      },
      floorsCount: {
        value: floorsCount,
        onChange: handleSearchChange('floorsCount', setFloorsCount),
      },
      fromBuildYear: {
        value: fromBuildYear,
        onChange: handleSearchChange('fromBuildYear', setFromBuildYear),
        onReset: handleSearchReset('fromBuildYear', setFromBuildYear),
      },
      toBuildYear: {
        value: toBuildYear,
        onChange: handleSearchChange('toBuildYear', setToBuildYear),
        onReset: handleSearchReset('toBuildYear', setToBuildYear),
      },
      buildingAge: {
        value: buildingAge,
        onChange: handleSearchChange('buildingAge', setBuildingAge),
      },
      buildingUnitFunction: {
        value: buildingUnitFunction,
        onChange: handleSearchChange('buildingUnitFunction', setBuildingUnitFunction),
      },
      fromMunicipalArea: {
        value: fromMunicipalArea,
        onChange: handleSearchChange('fromMunicipalArea', setFromMunicipalArea),
      },
      toMunicipalArea: {
        value: toMunicipalArea,
        onChange: handleSearchChange('toMunicipalArea', setToMunicipalArea),
      },
      soldShare: {
        value: soldShare,
        onChange: handleSearchChange('soldShare', setSoldShare),
      },
      parking: {
        value: parking,
        onChange: handleSearchChange('parking', setParking),
      },
      elevator: {
        value: elevator,
        onChange: handleSearchChange('elevator', setElevator),
      },
      gallery: {
        value: gallery,
        onChange: handleSearchChange('gallery', setGallery),
      },
      yard: {
        value: yard,
        onChange: handleSearchChange('yard', setYard),
      },
      storage: {
        value: storage,
        onChange: handleSearchChange('storage', setStorage),
      },
      roof: {
        value: roof,
        onChange: handleSearchChange('roof', setRoof),
      },
      lot: {
        value: lot,
        onChange: handleSearchChange('lot', setLot),
      },
      removeDuplicates: {
        value: removeDuplicates,
        onChange: handleSearchChange('removeDuplicates', setRemoveDuplicates),
      },
    }
  };

  return (
    <TaxAuthorityContext.Provider value={value}>
      <FormikProvider value={formik}>
        {children}
      </FormikProvider>
    </TaxAuthorityContext.Provider>
  );
};